import * as React from "react";
import { niceDate } from "@/lib/util";
import { translate } from "@/modules/shared/IntlProviderWrapper";

import type { PlanningTableColumnId, PlanningTableColumn, TableSize } from "../../viewsCommon";

type PlanningTableColumnDefinition = Omit<PlanningTableColumn, "id" | "value"> & {
    translation_key?: string;
    translate?: () => string;
};

type PlanningTableColumnDefinitionDictionary = { [key: PlanningTableColumnId]: PlanningTableColumnDefinition };

class PlanningTableColumns {
    static SMALL_TABLE_SIZE_THRESHOLD = 900;
    static MINI_TABLE_SIZE_THRESHOLD = 450;

    static COLUMN_DEFINITIONS: PlanningTableColumnDefinitionDictionary = {
        advanced_menu: {},
        workorder: {
            translation_key: "OrderTableProduction.external_id",
        },
        material: {
            translate: () => translate("common.id", "ID") + " - " + translate("common.material", "Material"),
        },
        material_id: {
            translation_key: "common.id",
            align: "right",
        },
        material_title: {
            translation_key: "common.material",
        },
        material_deadline: {
            translation_key: "common.deadline",
        },
        material_tool_setup: {
            translation_key: "common.tool_setup",
            min_width: 100,
        },
        unit: {
            translation_key: "common.quantity",
            min_width: 95,
            align: "right",
        },
        earliest_start: {
            translation_key: "OrderTableProduction.earliest_start",
            min_width: 85,
            align: "right",
        },
        earliest_end: {
            translation_key: "OrderTableProduction.earliest_end",
            min_width: 85,
            align: "right",
        },
        initial_earliest_start: {
            translation_key: "OrderTableProduction.initial_earliest_start",
            min_width: 85,
            align: "right",
        },
        initial_earliest_end: {
            translation_key: "OrderTableProduction.initial_earliest_end",
            min_width: 85,
            align: "right",
        },
        comment_inline: {
            translation_key: "common.comment",
            min_width: 200,
        },
        editorder: {},
        moveorder_start: {},
        moveorder_end: {},
        toolbar: {},
    };

    static BIG_TABLE_COLUMN_IDS: PlanningTableColumnId[] = [
        "advanced_menu",
        "moveorder_start",
        "workorder",
        "material_id",
        "material_title",
        "unit",
        "comment_inline",
        "editorder",
        "moveorder_end",
    ];

    static SMALL_TABLE_COLUMN_IDS: PlanningTableColumnId[] = [
        "advanced_menu",
        "moveorder_start",
        "workorder",
        "material",
        "unit",
        "editorder",
        "moveorder_end",
    ];

    static MINI_TABLE_COLUMN_IDS: PlanningTableColumnId[] = [
        "advanced_menu",
        "moveorder_start",
        "workorder",
        "editorder",
        "moveorder_end",
    ];

    static createInstance() {
        this.instance = new this();
    }

    static getInstance() {
        if (!this.instance) {
            this.createInstance();
        }

        return this.instance;
    }

    static instance: PlanningTableColumns;

    // Instance properties
    column_definitions: PlanningTableColumnDefinitionDictionary = {};
    custom_column_ids: PlanningTableColumnId[] = [];
    column_ids: Record<TableSize, PlanningTableColumnId[]> = {
        big: [...PlanningTableColumns.BIG_TABLE_COLUMN_IDS],
        small: [...PlanningTableColumns.SMALL_TABLE_COLUMN_IDS],
        mini: [...PlanningTableColumns.MINI_TABLE_COLUMN_IDS],
    };

    constructor() {
        this.column_definitions = { ...PlanningTableColumns.COLUMN_DEFINITIONS };
    }

    addColumnIds(new_column_ids: string[], params: { after_column_id: PlanningTableColumnId; table_size: TableSize }) {
        const { after_column_id, table_size } = params;

        if (!Object.prototype.hasOwnProperty.call(this.column_ids, table_size)) {
            throw new Error("Unknown type: " + table_size);
        }

        const selected_ids = this.column_ids[table_size];
        const after_index = this.column_ids[table_size].indexOf(after_column_id);
        if (after_index === -1) {
            throw new Error("Unknown 'after' column id: " + after_column_id);
        }

        this.column_ids[table_size] = [
            ...selected_ids.slice(0, after_index + 1),
            ...new_column_ids,
            ...selected_ids.slice(after_index + 1),
        ];
    }

    addCustomColumnDefinition(column_id: PlanningTableColumnId, definition: PlanningTableColumnDefinition) {
        this.column_definitions[column_id] = definition;
        this.custom_column_ids.push(column_id);
    }

    selectColumnIds(table_position: string, table_width_ratio: number): PlanningTableColumnId[] {
        const table_width = table_width_ratio * window.innerWidth;
        switch (table_position) {
            case "bottom":
                return window.innerWidth > PlanningTableColumns.SMALL_TABLE_SIZE_THRESHOLD
                    ? this.column_ids.big
                    : window.innerWidth > PlanningTableColumns.MINI_TABLE_SIZE_THRESHOLD
                      ? this.column_ids.small
                      : this.column_ids.mini;
            case "left":
                return table_width > PlanningTableColumns.SMALL_TABLE_SIZE_THRESHOLD
                    ? this.column_ids.big
                    : table_width > PlanningTableColumns.MINI_TABLE_SIZE_THRESHOLD
                      ? this.column_ids.small
                      : this.column_ids.mini;
            default:
                return [];
        }
    }

    getCustomColumnIds(): string[] {
        return this.custom_column_ids;
    }

    getColumnsFromIds(ids: PlanningTableColumnId[]): PlanningTableColumn[] {
        return ids.map(id => {
            const definition = this.column_definitions[id];
            if (!definition) {
                console.warn(`Column definition not found for id: ${id}`);
                return { id, value: "" };
            }

            const { translation_key, translate: getCustomTranslation = () => "", ...column_props } = definition;
            const value = translation_key ? translate(translation_key) : getCustomTranslation();
            return { ...column_props, id, value };
        });
    }

    // public
    getColumns(table_position: string, table_width_ratio: number): PlanningTableColumn[] {
        return this.getColumnsFromIds(this.selectColumnIds(table_position, table_width_ratio));
    }

    getUnscheduledColumns(): PlanningTableColumn[] {
        return this.getColumnsFromIds(this.column_ids.small);
    }

    format(value: string, column_id: PlanningTableColumnId): React.ReactNode {
        if (!value) {
            return "";
        }

        const column_def = this.column_definitions[column_id];
        if (!column_def || column_def.type !== "timestamp") {
            return value;
        }

        const date = new Date(Number(value));
        if (Number.isNaN(date.getTime())) {
            console.warn(`Invalid date created from timestamp: ${value}`);
            return value;
        }

        return <span className="nowrap">{niceDate(date)}</span>;
    }
}

export default PlanningTableColumns;
export type { PlanningTableColumnDefinition };
